import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Button, Input } from "@/components/ui";
import { useAuthContext } from "@/contexts/AuthContext";
import { createContactRequest, type ContactIntent } from "@/lib/firestore/contactRequests";
import { LogoMark, PressEyebrow, PressPanel, StatusPill } from "@/components/pullpress/PressVisuals";

const INTENTS: readonly ContactIntent[] = ["consultation", "sales", "support"] as const;

export function ContactPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuthContext();
  const initialIntent = normalizeIntent(searchParams.get("intent"));
  const [intent, setIntent] = useState<ContactIntent>(initialIntent);
  const [name, setName] = useState(user?.displayName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [organization, setOrganization] = useState("");
  const [message, setMessage] = useState(defaultMessage(initialIntent));
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const heading = useMemo(() => {
    if (intent === "sales") return "Talk to us about finishing the book.";
    if (intent === "support") return "Tell us where the book stopped.";
    return "Plan the first book properly.";
  }, [intent]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      setError("Name, email, and message are required.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const id = await createContactRequest({
        intent,
        name: trimmedName,
        email: trimmedEmail,
        organization: organization.trim(),
        message: trimmedMessage,
        user_id: user?.uid ?? "",
      });
      setSubmittedId(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Contact request could not be submitted.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell variant="public">
      <div className="mx-auto grid max-w-[1120px] gap-5 px-4 py-8 sm:px-6 lg:grid-cols-[0.82fr_1fr] lg:px-8">
        <section className="rounded-[14px] border border-rule bg-paper p-5 lg:p-6">
          <div className="flex items-center gap-3">
            <LogoMark className="h-10 w-9 text-walnut" />
          <PressEyebrow>One clear next step.</PressEyebrow>
          </div>
          <h1 className="mt-8 max-w-[560px] font-display text-[clamp(2.2rem,4vw,3.8rem)] font-semibold leading-[1] tracking-as-tight text-ink-1">
            {heading}
          </h1>
          <p className="mt-4 max-w-[520px] font-text text-base leading-as-read text-ink-2">
            Tell us what you have, what you want the book to become, and what is stopping it now.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <ContactRoute label="Consultation." active={intent === "consultation"} onClick={() => setIntent("consultation")} />
            <ContactRoute label="Sales." active={intent === "sales"} onClick={() => setIntent("sales")} />
            <ContactRoute label="Support." active={intent === "support"} onClick={() => setIntent("support")} />
          </div>
          <div className="mt-6 rounded-as-2 border border-rule bg-paper-raised p-4">
            <PressEyebrow>What happens next.</PressEyebrow>
            <p className="mt-2 font-ui text-sm leading-as-read text-ink-2">
              We read the note and reply with the next practical move for the book.
            </p>
          </div>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <Link to="/signup"><Button variant="walnut">Start my book</Button></Link>
            <Link to="/pricing"><Button variant="secondary">See pricing</Button></Link>
          </div>
        </section>

        <PressPanel className="p-5 lg:p-6">
          <PressEyebrow>Tell us the situation.</PressEyebrow>
          {submittedId ? (
            <div className="mt-8">
              <StatusPill tone="ready">Submitted</StatusPill>
              <h2 className="mt-4 font-display text-3xl font-semibold text-ink-1">We have the book note.</h2>
              <p className="mt-3 font-ui text-sm leading-as-read text-ink-2">
                Your request ID is <span className="font-mono text-ink-1">{submittedId}</span>. We will reply with the next move.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link to="/signup"><Button variant="walnut">Start my book</Button></Link>
                <Link to="/pricing"><Button variant="secondary">See pricing</Button></Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              {error && (
                <div className="rounded-as-2 border border-flag bg-flag-tint p-3 font-mono text-[11px] uppercase tracking-as-eyebrow text-flag">
                  {error}
                </div>
              )}
              <Input label="Name" value={name} onChange={(event) => setName(event.target.value)} required />
              <Input label="Email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
              <Input label="Organization" value={organization} onChange={(event) => setOrganization(event.target.value)} />
              <label className="flex flex-col gap-[4px]">
                <span className="font-mono text-[11px] uppercase tracking-as-eyebrow text-ink-3">What should we know?</span>
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  className="min-h-[150px] rounded-as-1 border border-rule bg-paper-raised px-3 py-2 font-ui text-sm leading-as-read text-ink-1 outline-none focus:border-walnut"
                  required
                />
              </label>
              <Button type="submit" variant="walnut" size="lg" disabled={submitting} className="w-full">
                {submitting ? "Sending..." : "Send the note"}
              </Button>
            </form>
          )}
        </PressPanel>
      </div>
    </AppShell>
  );
}

function ContactRoute({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-11 rounded-as-2 border px-3 py-2 text-left font-mono text-[10px] uppercase tracking-as-eyebrow ${active ? "border-walnut bg-walnut-tint text-walnut-deep" : "border-rule bg-paper-raised text-ink-3"}`}
    >
      {label}
    </button>
  );
}

function normalizeIntent(value: string | null): ContactIntent {
  if (value && INTENTS.includes(value as ContactIntent)) return value as ContactIntent;
  return "consultation";
}

function defaultMessage(intent: ContactIntent): string {
  if (intent === "sales") return "I want to talk about finishing a book with PullPress.";
  if (intent === "support") return "My book is stuck here:";
  return "I want to plan my first book run.";
}
